import type { Dispatch, FC, PropsWithChildren, SetStateAction } from 'react';
import { createContext, useContext, useState } from 'react';
import { AUTH_TOKEN_KEY } from '~/constants';

type LogoutArgs = {
  isExpired?: boolean;
};
type AuthContextProps = {
  token: string | null;
  setToken: (token: string | null) => void;
  logout: (args?: LogoutArgs) => void;
  sessionExpired: boolean;
  setSessionExpired: Dispatch<SetStateAction<boolean>>;
};

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const [token, setTokenState] = useState<string | null>(sessionStorage.getItem(AUTH_TOKEN_KEY));
  const [sessionExpired, setSessionExpired] = useState<boolean>(false);

  const setToken = (newToken: string | null) => {
    if (newToken) {
      sessionStorage.setItem(AUTH_TOKEN_KEY, newToken);
    } else {
      sessionStorage.removeItem(AUTH_TOKEN_KEY);
    }
    setTokenState(newToken);
  };

  const logout = ({ isExpired }: LogoutArgs = { isExpired: true }) => {
    setSessionExpired(isExpired ?? false);
    setToken(null);
  };

  return (
    <AuthContext.Provider value={{ token, setToken, logout, sessionExpired, setSessionExpired }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
